import Commons from '@features/shared/libraries/commons';

export function importSharerJs() {
  return new Promise((resolve, reject) => {
    if (window.Sharer) return resolve(window.Sharer);

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/sharer.js@0.5.2/sharer.min.js';
    script.onload = () => resolve(window.Sharer);
    script.onerror = reject;
    document.body.appendChild(script);
  });
}

/**
 * Initialise sharer.js pour les réseaux standard ET attache nos handlers custom
 * pour les réseaux non supportés par sharer.js (instagram, snapchat...).
 * Fonctionne exactement comme sharer.js : scan du DOM sur les [data-sharer] elements.
 */
export function initSharrerWatch() {
  const handledElements = new WeakSet();

  const attachCustomHandlers = () => {
    // Laisser sharer.js gérer ses propres éléments
    window.Sharer?.init?.();

    // Scanner tous les [data-sharer] du DOM
    document.querySelectorAll('[data-sharer]').forEach((el) => {
      const type = el.getAttribute('data-sharer');
      if (!CUSTOM_SHARE_TYPES.includes(type)) return;
      if (handledElements.has(el)) return; // déjà enregistré

      handledElements.add(el);

      el.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const text = el.getAttribute('data-title') || '';
        const url = el.getAttribute('data-url') || window.location.href;

        handleSocialShare(type, { text, url });
      });
    });
  };

  // Scan régulier (comme sharer.js) pour attraper les éléments montés dynamiquement
  setInterval(attachCustomHandlers, 1000);
}

/**
 * Partager vers Instagram.
 * Instagram ne propose pas de Web Intent direct pour les posts.
 * La stratégie : copier le texte + URL dans le presse-papier et ouvrir l'app.
 * Sur iOS/Android, le schéma `instagram://` ouvre l'app nativement.
 */
export function shareToInstagram({ text = '', url = '' } = {}) {
  const fullText = text ? `${text}\n\n${url}` : url;

  // Copier dans le presse-papier pour que l'utilisateur puisse coller dans Instagram
  Commons.copyToClipboard(
    fullText,
    () => {},
    () => {}
  );

  // Tenter d'ouvrir l'app Instagram en natif (mobile)
  const appScheme = 'instagram://app';
  const webFallback = 'https://www.instagram.com/';

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = appScheme;
  document.body.appendChild(iframe);

  setTimeout(() => {
    document.body.removeChild(iframe);
    // Si l'app ne s'est pas ouverte, aller sur le site web
    window.open(webFallback, '_blank', 'noopener');
  }, 1500);
}

/**
 * Partager vers Snapchat.
 * Snapchat propose une URL de partage via Creative Kit :
 * https://www.snapchat.com/scan?attachmentUrl=<URL>
 * Sur mobile avec l'app installée, ça ouvre Snapchat directement.
 */
export function shareToSnapchat({ url = '', text = '' } = {}) {
  const encodedUrl = encodeURIComponent(url || window.location.href);
  const snapUrl = `https://www.snapchat.com/scan?attachmentUrl=${encodedUrl}`;
  window.open(snapUrl, '_blank', 'noopener');
}

/**
 * Partager vers TikTok.
 * Comme Instagram, pas de Web Intent facile. Copier le contenu et ouvrir l'app.
 * Schéma (international) : snssdk1233://
 */
export function shareToTikTok({ text = '', url = '' } = {}) {
  const fullText = text ? `${text}\n\n${url}` : url;

  Commons.copyToClipboard(
    fullText,
    () => {},
    () => {}
  );

  const appScheme = 'snssdk1233://';
  const webFallback = 'https://www.tiktok.com/';

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = appScheme;
  document.body.appendChild(iframe);

  setTimeout(() => {
    document.body.removeChild(iframe);
    window.open(webFallback, '_blank', 'noopener');
  }, 1500);
}

/**
 * Dispatcher central pour tous les réseaux.
 * Gère sharer.js pour les réseaux supportés, et nos fonctions custom pour le reste.
 */
export function handleSocialShare(
  type,
  { text = '', url = '', title = '' } = {}
) {
  if (type === 'instagram') {
    return shareToInstagram({ text, url });
  }
  if (type === 'snapchat') {
    return shareToSnapchat({ url, text });
  }
  if (type === 'tiktok') {
    return shareToTikTok({ text, url });
  }

  // Pour les autres (whatsapp, facebook, twitter, telegram, email),
  // on laisse sharer.js faire le travail via les data-attributes.
  // Cette fonction est juste un no-op pour ces cas-là.
}

/** Les réseaux qui nécessitent un handler custom (pas gérés par sharer.js) */
export const CUSTOM_SHARE_TYPES = ['instagram', 'snapchat', 'tiktok'];
